import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ArrowLeft, Plus, Trash2, Edit2, ChevronDown, ChevronRight,
  BookOpen, Video, FileText, HelpCircle, Save, X, Check,
} from 'lucide-react';
import toast from 'react-hot-toast';

const LESSON_TYPES = [
  { value: 'video',    label: 'Vidéo' },
  { value: 'text',     label: 'Texte' },
  { value: 'document', label: 'Document' },
];

const TYPE_ICON = { video: Video, text: FileText, document: FileText };

const defaultLesson = () => ({
  title: '', type: 'video', estimatedDuration: 5,
  isPreview: false, isPublished: false,
  content: { videoUrl: '', text: '', documentUrl: '' },
});

const defaultEval = () => ({
  title: '', description: '', passingScore: 70, timeLimit: '', maxAttempts: 3,
  questions: [{
    id: Date.now(), text: '', type: 'multiple_choice',
    options: ['', '', '', ''], correctAnswer: 0, points: 1,
  }],
});

// ── Question editor ──────────────────────────────────────────────────────────
const QuestionEditor = ({ questions, onChange }) => {
  const add = () => onChange([...questions, {
    id: Date.now(), text: '', type: 'multiple_choice',
    options: ['', '', '', ''], correctAnswer: 0, points: 1,
  }]);
  const remove = (idx) => onChange(questions.filter((_, i) => i !== idx));
  const update = (idx, field, value) =>
    onChange(questions.map((q, i) => i === idx ? { ...q, [field]: value } : q));
  const updateOption = (qi, oi, value) =>
    onChange(questions.map((q, i) =>
      i === qi ? { ...q, options: q.options.map((o, j) => j === oi ? value : o) } : q));
  const addOption = (qi) =>
    onChange(questions.map((q, i) =>
      i === qi ? { ...q, options: [...(q.options || []), ''] } : q));
  const removeOption = (qi, oi) =>
    onChange(questions.map((q, i) => {
      if (i !== qi) return q;
      const newOptions = q.options.filter((_, j) => j !== oi);
      const newCorrect = q.correctAnswer >= oi && q.correctAnswer > 0
        ? q.correctAnswer - 1
        : q.correctAnswer;
      return { ...q, options: newOptions, correctAnswer: Math.min(newCorrect, newOptions.length - 1) };
    }));

  return (
    <div className="space-y-4">
      {questions.map((q, qi) => (
        <div key={q.id || qi} className="border border-gray-200 rounded-lg p-4">
          <div className="flex items-start gap-2 mb-3">
            <span className="text-sm font-medium text-gray-500 mt-2 w-6 flex-shrink-0">{qi + 1}.</span>
            <select
              className="border border-gray-200 rounded px-2 py-2 text-xs text-gray-600"
              value={q.type}
              onChange={e => update(qi, 'type', e.target.value)}
            >
              <option value="multiple_choice">QCM</option>
              <option value="true_false">Vrai/Faux</option>
            </select>
            <input
              className="flex-1 border border-gray-300 rounded px-3 py-2 text-sm"
              placeholder="Énoncé de la question…"
              value={q.text}
              onChange={e => update(qi, 'text', e.target.value)}
            />
            <input
              type="number" min="1" max="10"
              className="w-16 border border-gray-300 rounded px-2 py-2 text-sm"
              title="Points"
              value={q.points}
              onChange={e => update(qi, 'points', parseInt(e.target.value) || 1)}
            />
            <button onClick={() => remove(qi)} className="p-2 text-red-400 hover:text-red-600">
              <X className="w-4 h-4" />
            </button>
          </div>

          {q.type === 'multiple_choice' && (
            <div className="ml-8 space-y-2">
              {(q.options || []).map((opt, oi) => (
                <div key={oi} className="flex items-center gap-2">
                  <input
                    type="radio" name={`q${qi}-${q.id}`}
                    checked={q.correctAnswer === oi}
                    onChange={() => update(qi, 'correctAnswer', oi)}
                    className="text-primary flex-shrink-0"
                  />
                  <input
                    className="flex-1 border border-gray-200 rounded px-2 py-1 text-sm"
                    placeholder={`Option ${oi + 1}`}
                    value={opt}
                    onChange={e => updateOption(qi, oi, e.target.value)}
                  />
                  {(q.options || []).length > 2 && (
                    <button
                      onClick={() => removeOption(qi, oi)}
                      className="p-1 text-gray-300 hover:text-red-500 flex-shrink-0"
                      title="Supprimer cette option"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              ))}
              <div className="flex items-center justify-between mt-1">
                <p className="text-xs text-gray-400">Sélectionnez la bonne réponse (radio)</p>
                <button
                  onClick={() => addOption(qi)}
                  className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 font-medium"
                >
                  <Plus className="w-3 h-3" /> Option
                </button>
              </div>
            </div>
          )}

          {q.type === 'true_false' && (
            <div className="ml-8 space-y-1">
              {['Vrai', 'Faux'].map((label, oi) => (
                <label key={oi} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio" name={`q${qi}-${q.id}`}
                    checked={q.correctAnswer === oi}
                    onChange={() => update(qi, 'correctAnswer', oi)}
                  />
                  <span className="text-sm">{label}</span>
                </label>
              ))}
            </div>
          )}
        </div>
      ))}
      <button
        onClick={add}
        className="flex items-center gap-2 text-sm text-primary hover:text-primary/80 font-medium"
      >
        <Plus className="w-4 h-4" /> Ajouter une question
      </button>
    </div>
  );
};

// ── Lesson modal ─────────────────────────────────────────────────────────────
const LessonModal = ({ lesson, onSave, onClose }) => {
  const [form, setForm] = useState(() => {
    if (!lesson) return defaultLesson();
    return {
      title: lesson.title || '',
      type: lesson.type || 'video',
      estimatedDuration: lesson.estimatedDuration || lesson.estimated_duration || 5,
      isPreview: lesson.isPreview || lesson.is_preview || false,
      isPublished: lesson.isPublished || lesson.is_published || false,
      content: {
        videoUrl: lesson.content?.videoUrl || lesson.content_video_url || '',
        text: lesson.content?.text || lesson.content_text || '',
        documentUrl: lesson.content?.documentUrl || lesson.content_document_url || '',
      },
    };
  });
  const [saving, setSaving] = useState(false);

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));
  const setContent = (key, val) => setForm(f => ({ ...f, content: { ...f.content, [key]: val } }));

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error('Titre requis'); return; }
    setSaving(true);
    try { await onSave(form); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b">
          <h3 className="font-semibold text-gray-900">
            {lesson ? 'Modifier la leçon' : 'Ajouter une leçon'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Titre *</label>
            <input
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              value={form.title}
              onChange={e => set('title', e.target.value)}
              placeholder="Titre de la leçon"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                value={form.type}
                onChange={e => set('type', e.target.value)}
              >
                {LESSON_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Durée (min)</label>
              <input
                type="number" min="1"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                value={form.estimatedDuration}
                onChange={e => set('estimatedDuration', parseInt(e.target.value) || 5)}
              />
            </div>
          </div>

          {form.type === 'video' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">URL vidéo</label>
              <input
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                value={form.content.videoUrl}
                onChange={e => setContent('videoUrl', e.target.value)}
                placeholder="https://youtube.com/…"
              />
            </div>
          )}
          {form.type === 'text' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Contenu</label>
              <textarea
                rows={6}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                value={form.content.text}
                onChange={e => setContent('text', e.target.value)}
                placeholder="Contenu de la leçon…"
              />
            </div>
          )}
          {form.type === 'document' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">URL document</label>
              <input
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                value={form.content.documentUrl}
                onChange={e => setContent('documentUrl', e.target.value)}
                placeholder="https://…/document.pdf"
              />
            </div>
          )}

          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox" checked={form.isPreview}
                onChange={e => set('isPreview', e.target.checked)} className="rounded"
              />
              Aperçu gratuit
            </label>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox" checked={form.isPublished}
                onChange={e => set('isPublished', e.target.checked)} className="rounded"
              />
              Publier
            </label>
          </div>
        </div>
        <div className="flex justify-end gap-3 p-5 border-t">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">
            Annuler
          </button>
          <button
            onClick={handleSave} disabled={saving}
            className="px-4 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 flex items-center gap-2"
          >
            {saving
              ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              : <Save className="w-4 h-4" />}
            Enregistrer
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Evaluation modal ─────────────────────────────────────────────────────────
const EvalModal = ({ evaluation, onSave, onClose }) => {
  const [form, setForm] = useState(() => {
    if (!evaluation) return defaultEval();
    return {
      title: evaluation.title || '',
      description: evaluation.description || '',
      passingScore: evaluation.passing_score || 70,
      timeLimit: evaluation.time_limit_minutes || '',
      maxAttempts: evaluation.max_attempts || 3,
      questions: (evaluation.questions || []).map((q, i) => ({ ...q, id: q.id || i })),
    };
  });
  const [saving, setSaving] = useState(false);

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error('Titre requis'); return; }
    if (form.questions.length === 0) { toast.error('Au moins une question requise'); return; }
    setSaving(true);
    try { await onSave(form); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b">
          <h3 className="font-semibold text-gray-900">
            {evaluation ? "Modifier l'évaluation" : 'Ajouter une évaluation / Quiz'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Titre *</label>
            <input
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              value={form.title}
              onChange={e => set('title', e.target.value)}
              placeholder="Titre de l'évaluation"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <input
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              value={form.description}
              onChange={e => set('description', e.target.value)}
              placeholder="Description optionnelle"
            />
          </div>
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Score requis (%)</label>
              <input
                type="number" min="0" max="100"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                value={form.passingScore}
                onChange={e => set('passingScore', parseInt(e.target.value) || 70)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Limite (min)</label>
              <input
                type="number" min="1"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                value={form.timeLimit}
                onChange={e => set('timeLimit', e.target.value ? parseInt(e.target.value) : '')}
                placeholder="Sans limite"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tentatives max</label>
              <input
                type="number" min="1"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                value={form.maxAttempts}
                onChange={e => set('maxAttempts', parseInt(e.target.value) || 3)}
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">Questions</label>
            <QuestionEditor
              questions={form.questions}
              onChange={qs => set('questions', qs)}
            />
          </div>
        </div>
        <div className="flex justify-end gap-3 p-5 border-t">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">
            Annuler
          </button>
          <button
            onClick={handleSave} disabled={saving}
            className="px-4 py-2 text-sm bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 flex items-center gap-2"
          >
            {saving
              ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              : <Save className="w-4 h-4" />}
            Enregistrer
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Inline module form ───────────────────────────────────────────────────────
const ModuleForm = ({ initial, onSave, onCancel }) => {
  const [title, setTitle] = useState(initial?.title || '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (!title.trim()) { toast.error('Titre requis'); return; }
    setSaving(true);
    try { await onSave({ title }); }
    finally { setSaving(false); }
  };

  return (
    <div className="flex items-center gap-2 p-3 bg-blue-50 rounded-lg border border-blue-100">
      <input
        autoFocus
        className="flex-1 border border-gray-300 rounded px-3 py-1.5 text-sm"
        placeholder="Titre du module"
        value={title}
        onChange={e => setTitle(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') handleSave(); if (e.key === 'Escape') onCancel(); }}
      />
      <button
        onClick={handleSave} disabled={saving}
        className="p-1.5 bg-primary text-white rounded hover:bg-primary/90 disabled:opacity-50"
      >
        <Check className="w-4 h-4" />
      </button>
      <button onClick={onCancel} className="p-1.5 text-gray-400 hover:text-gray-600 rounded">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

// ── Main page ────────────────────────────────────────────────────────────────
const CourseContentManager = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();

  const [courseTitle, setCourseTitle] = useState('');
  const [modules, setModules] = useState([]);
  const [evaluations, setEvaluations] = useState({});
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  const [addingModule, setAddingModule] = useState(false);
  const [editingModule, setEditingModule] = useState(null);
  const [lessonModal, setLessonModal] = useState(null);
  const [evalModal, setEvalModal] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [courseRes, modulesRes, evalsRes] = await Promise.all([
        axios.get(`/api/courses/${courseId}`),
        axios.get(`/api/modules/course/${courseId}`),
        axios.get(`/api/evaluations/course/${courseId}`),
      ]);
      setCourseTitle(courseRes.data.course?.title || '');
      const mods = modulesRes.data.modules || [];
      setModules(mods);
      const exp = {};
      mods.forEach(m => { exp[m.id] = true; });
      setExpanded(exp);
      const byModule = {};
      (evalsRes.data.evaluations || []).forEach(ev => {
        if (!byModule[ev.module_id]) byModule[ev.module_id] = [];
        byModule[ev.module_id].push(ev);
      });
      setEvaluations(byModule);
    } catch {
      toast.error('Erreur chargement du contenu');
    } finally {
      setLoading(false);
    }
  }, [courseId]);

  useEffect(() => { loadData(); }, [loadData]);

  // Module CRUD
  const addModule = async ({ title }) => {
    try {
      const { data } = await axios.post('/api/modules', { courseId, title, order: modules.length });
      setModules(m => [...m, { ...data.module, lessons: [] }]);
      setExpanded(e => ({ ...e, [data.module.id]: true }));
      setAddingModule(false);
      toast.success('Module ajouté');
    } catch {
      toast.error('Erreur ajout module');
    }
  };

  const updateModule = async ({ title }) => {
    try {
      await axios.put(`/api/modules/${editingModule.id}`, { title });
      setModules(m => m.map(mod => mod.id === editingModule.id ? { ...mod, title } : mod));
      setEditingModule(null);
      toast.success('Module mis à jour');
    } catch {
      toast.error('Erreur mise à jour module');
    }
  };

  const deleteModule = async (mod) => {
    if (!window.confirm(`Supprimer le module "${mod.title}" et tout son contenu ?`)) return;
    try {
      await axios.delete(`/api/modules/${mod.id}`);
      setModules(m => m.filter(x => x.id !== mod.id));
      toast.success('Module supprimé');
    } catch {
      toast.error('Erreur suppression module');
    }
  };

  // Lesson CRUD
  const saveLesson = async (form) => {
    const { moduleId, lesson } = lessonModal;
    try {
      if (lesson) {
        const { data } = await axios.put(`/api/lessons/${lesson.id}`, form);
        setModules(mods => mods.map(m =>
          m.id === moduleId ? { ...m, lessons: m.lessons.map(l => l.id === lesson.id ? data.lesson : l) } : m
        ));
        toast.success('Leçon mise à jour');
      } else {
        const order = (modules.find(m => m.id === moduleId)?.lessons || []).length;
        const { data } = await axios.post('/api/lessons', { moduleId, courseId, order, ...form });
        setModules(mods => mods.map(m =>
          m.id === moduleId ? { ...m, lessons: [...m.lessons, data.lesson] } : m
        ));
        toast.success('Leçon ajoutée');
      }
      setLessonModal(null);
    } catch {
      toast.error('Erreur sauvegarde leçon');
    }
  };

  const deleteLesson = async (lesson, moduleId) => {
    if (!window.confirm(`Supprimer la leçon "${lesson.title}" ?`)) return;
    try {
      await axios.delete(`/api/lessons/${lesson.id}`);
      setModules(mods => mods.map(m =>
        m.id === moduleId ? { ...m, lessons: m.lessons.filter(l => l.id !== lesson.id) } : m
      ));
      toast.success('Leçon supprimée');
    } catch {
      toast.error('Erreur suppression leçon');
    }
  };

  // Evaluation CRUD
  const saveEval = async (form) => {
    const { moduleId, evaluation } = evalModal;
    const payload = { ...form, timeLimit: form.timeLimit === '' ? null : form.timeLimit };
    try {
      if (evaluation) {
        const { data } = await axios.put(`/api/evaluations/${evaluation.id}`, payload);
        setEvaluations(prev => ({
          ...prev,
          [moduleId]: (prev[moduleId] || []).map(e => e.id === evaluation.id ? data.evaluation : e),
        }));
        toast.success('Évaluation mise à jour');
      } else {
        const { data } = await axios.post('/api/evaluations', { moduleId, courseId, ...payload });
        setEvaluations(prev => ({
          ...prev,
          [moduleId]: [...(prev[moduleId] || []), data.evaluation],
        }));
        toast.success('Évaluation ajoutée');
      }
      setEvalModal(null);
    } catch {
      toast.error('Erreur sauvegarde évaluation');
    }
  };

  const deleteEval = async (ev, moduleId) => {
    if (!window.confirm(`Supprimer l'évaluation "${ev.title}" ?`)) return;
    try {
      await axios.delete(`/api/evaluations/${ev.id}`);
      setEvaluations(prev => ({
        ...prev,
        [moduleId]: (prev[moduleId] || []).filter(e => e.id !== ev.id),
      }));
      toast.success('Évaluation supprimée');
    } catch {
      toast.error('Erreur suppression évaluation');
    }
  };

  if (loading) {
    return (
      <div className="min-h-full bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-full bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-8">

        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate('/instructor')}
            className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-gray-900">Contenu du cours</h1>
            <p className="text-gray-500 text-sm">{courseTitle}</p>
          </div>
          <button
            onClick={() => setAddingModule(true)}
            className="flex items-center gap-2 bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary/90 text-sm font-medium"
          >
            <Plus className="w-4 h-4" /> Ajouter un module
          </button>
        </div>

        {addingModule && (
          <div className="mb-4">
            <ModuleForm onSave={addModule} onCancel={() => setAddingModule(false)} />
          </div>
        )}

        {modules.length === 0 && !addingModule && (
          <div className="text-center py-16 bg-white rounded-xl border-2 border-dashed border-gray-200">
            <BookOpen className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-600 font-medium mb-1">Aucun module</p>
            <p className="text-sm text-gray-400 mb-4">Créez des modules pour organiser leçons et évaluations</p>
            <button
              onClick={() => setAddingModule(true)}
              className="text-primary text-sm font-medium hover:underline"
            >
              + Créer le premier module
            </button>
          </div>
        )}

        <div className="space-y-4">
          {modules.map((mod, modIdx) => {
            const isExpanded = expanded[mod.id] !== false;
            const modEvals = evaluations[mod.id] || [];
            const lessonsCount = mod.lessons?.length || 0;
            const evalsCount = modEvals.length;

            return (
              <div key={mod.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">

                {/* Module header */}
                {editingModule?.id === mod.id ? (
                  <div className="p-4">
                    <ModuleForm initial={mod} onSave={updateModule} onCancel={() => setEditingModule(null)} />
                  </div>
                ) : (
                  <div
                    className="flex items-center gap-3 p-4 cursor-pointer hover:bg-gray-50 select-none"
                    onClick={() => setExpanded(e => ({ ...e, [mod.id]: !isExpanded }))}
                  >
                    {isExpanded
                      ? <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
                      : <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <span className="font-semibold text-gray-900">
                        {modIdx + 1}. {mod.title}
                      </span>
                      <span className="ml-3 text-xs text-gray-400">
                        {lessonsCount} leçon{lessonsCount !== 1 ? 's' : ''} · {evalsCount} évaluation{evalsCount !== 1 ? 's' : ''}
                      </span>
                    </div>
                    <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                      <button
                        onClick={() => setEditingModule(mod)}
                        className="p-1.5 text-gray-400 hover:text-primary hover:bg-gray-100 rounded"
                        title="Renommer"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => deleteModule(mod)}
                        className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded"
                        title="Supprimer"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}

                {/* Module body */}
                {isExpanded && (
                  <div className="border-t border-gray-100">

                    {/* Lessons section */}
                    <div className="p-4 border-b border-gray-50">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          Leçons
                        </h4>
                        <button
                          onClick={() => setLessonModal({ mode: 'add', moduleId: mod.id })}
                          className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 font-medium"
                        >
                          <Plus className="w-3.5 h-3.5" /> Ajouter
                        </button>
                      </div>

                      {lessonsCount === 0 ? (
                        <p className="text-sm text-gray-400 italic py-1">Aucune leçon</p>
                      ) : (
                        <div className="space-y-1">
                          {(mod.lessons || []).map((lesson, li) => {
                            const Icon = TYPE_ICON[lesson.type] || FileText;
                            const published = lesson.isPublished || lesson.is_published;
                            return (
                              <div
                                key={lesson.id}
                                className="flex items-center gap-3 py-2 px-3 rounded-lg hover:bg-gray-50 group"
                              >
                                <span className="text-xs text-gray-400 w-5 text-right">{li + 1}.</span>
                                <Icon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                <span className="flex-1 text-sm text-gray-800">{lesson.title}</span>
                                <span className="text-xs text-gray-400">
                                  {lesson.estimatedDuration || lesson.estimated_duration || 0} min
                                </span>
                                <span className={`text-xs px-2 py-0.5 rounded-full ${
                                  published
                                    ? 'text-green-700 bg-green-50'
                                    : 'text-gray-500 bg-gray-100'
                                }`}>
                                  {published ? 'Publié' : 'Brouillon'}
                                </span>
                                <div className="hidden group-hover:flex items-center gap-1">
                                  <button
                                    onClick={() => setLessonModal({ mode: 'edit', moduleId: mod.id, lesson })}
                                    className="p-1 text-gray-400 hover:text-primary rounded"
                                    title="Modifier"
                                  >
                                    <Edit2 className="w-3.5 h-3.5" />
                                  </button>
                                  <button
                                    onClick={() => deleteLesson(lesson, mod.id)}
                                    className="p-1 text-gray-400 hover:text-red-500 rounded"
                                    title="Supprimer"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>

                    {/* Evaluations section */}
                    <div className="p-4">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          Évaluations / Quiz
                        </h4>
                        <button
                          onClick={() => setEvalModal({ mode: 'add', moduleId: mod.id })}
                          className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 font-medium"
                        >
                          <Plus className="w-3.5 h-3.5" /> Ajouter
                        </button>
                      </div>

                      {evalsCount === 0 ? (
                        <p className="text-sm text-gray-400 italic py-1">Aucune évaluation</p>
                      ) : (
                        <div className="space-y-1">
                          {modEvals.map(ev => (
                            <div
                              key={ev.id}
                              className="flex items-center gap-3 py-2 px-3 rounded-lg hover:bg-gray-50 group"
                            >
                              <HelpCircle className="w-4 h-4 text-purple-400 flex-shrink-0" />
                              <span className="flex-1 text-sm text-gray-800">{ev.title}</span>
                              <span className="text-xs text-gray-400">
                                {(ev.questions || []).length} question{(ev.questions || []).length !== 1 ? 's' : ''}
                              </span>
                              <span className="text-xs text-gray-400">Score : {ev.passing_score}%</span>
                              <div className="hidden group-hover:flex items-center gap-1">
                                <button
                                  onClick={() => setEvalModal({ mode: 'edit', moduleId: mod.id, evaluation: ev })}
                                  className="p-1 text-gray-400 hover:text-primary rounded"
                                  title="Modifier"
                                >
                                  <Edit2 className="w-3.5 h-3.5" />
                                </button>
                                <button
                                  onClick={() => deleteEval(ev, mod.id)}
                                  className="p-1 text-gray-400 hover:text-red-500 rounded"
                                  title="Supprimer"
                                >
                                  <Trash2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {lessonModal && (
        <LessonModal
          lesson={lessonModal.lesson}
          onSave={saveLesson}
          onClose={() => setLessonModal(null)}
        />
      )}
      {evalModal && (
        <EvalModal
          evaluation={evalModal.evaluation}
          onSave={saveEval}
          onClose={() => setEvalModal(null)}
        />
      )}
    </div>
  );
};

export default CourseContentManager;
